import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { format } from 'date-fns';
import { get as _get } from 'lodash';
import { Observable } from 'rxjs';

import { env } from '@amfam/environment';
import { Risk } from '@amfam/policy/models';
import { Party } from '@amfam/shared/models';

import { KydSmartphoneEnrollmentContactDetail, KydSmartphoneEnrollmentOperator } from '../models';
import {
  EnrollKydOperatorPayload,
  EnrollKydPolicyData,
  EnrollKydPolicyRequest,
  UpdateKydOperatorPhonePayload
} from '../models/kyd-partial-household.model';
import { UBI_PROGRAM } from '../models/kyd-program-category.model';

@Injectable({
  providedIn: 'root'
})
export class KydPartialHouseholdService {
  constructor(private http: HttpClient) {}

  public enrollPolicy(payload: EnrollKydPolicyRequest): Observable<any> {
    const endpoint = env.ubiApi + 'enrollments';
    const request: any = {
      EnrollmentRequest: payload
    };
    return this.http.post(endpoint, request);
  }

  public buildEnrollPolicyRequest(
    policy: EnrollKydPolicyData,
    party: Party,
    customerId: string
  ): EnrollKydPolicyRequest {
    const contractState = this.getContractState(policy);
    const shippingAddress = this.buildAddress(_get(party, 'addresses[0]', {}), contractState);

    const enrollmentRequest: EnrollKydPolicyRequest = {
      customerIdentifier: customerId,
      policyNumber: policy.policyNumber,
      termsAcceptedIndicator: 'Y',
      acknowledgementDate: format(new Date(), 'YYYY-MM-DD'),
      requestDate: format(new Date(), 'YYYY-MM-DD'),
      emailAddress: party.primaryEmail.emailAddress,
      phoneNumber: party.primaryPhone.tenDigitPhoneNumber,
      policyEffectiveDate: format(policy.effectiveDate, 'YYYY-MM-DD'),
      policyExpireDate: format(policy.endEffectiveDate, 'YYYY-MM-DD'),
      policyState: contractState,
      shippingAddress: shippingAddress,
      operators: this.buildOperatorsList(policy),
      ubiProgramCategoryCode: UBI_PROGRAM.TRUE_MOTION,
      callingApp: 'KYDENROLLAPI',
      optInReasonCode: '1001',
      optInType: '1'
    };

    return enrollmentRequest;
  }

  public unenrollPolicy(enrollmentId: number): Observable<any> {
    const endpoint =
      env.ubiApi +
      'enrollments/' +
      enrollmentId +
      '?OptOutReasonCode=2002&optOutType=NamedInsuredVoluntarilyOptedOut';
    return this.http.delete(endpoint);
  }

  public enrollOperator(data: EnrollKydOperatorPayload): Observable<any> {
    const endpoint = env.ubiApi + 'enroll/operator/' + data.policyNumber;
    const payload = {
      TMRequest: {
        phoneNumber: data.phoneNumber,
        cdhId: data.cdhId,
        originSystem: 'MYACCOUNT'
      }
    };

    return this.http.post(endpoint, payload);
  }

  public unenrollOperator(data: EnrollKydOperatorPayload): Observable<any> {
    const endpoint = env.ubiApi + 'unenroll/operator/' + data.policyNumber;
    const payload = {
      TMRequest: {
        phoneNumber: data.phoneNumber,
        cdhId: data.cdhId,
        originSystem: 'MYACCOUNT',
        reason: 'text'
      }
    };

    return this.http.post(endpoint, payload);
  }

  public updatePhone(data: UpdateKydOperatorPhonePayload): Observable<any> {
    const endpoint = env.ubiApi + 'operator/' + data.operatorId;
    const payload = {
      TMRequest: {
        phoneNumber: data.phoneNumber,
        cdhId: data.cdhId
      }
    };

    return this.http.put(endpoint, payload);
  }

  private getContractState(kydPolicy: EnrollKydPolicyData): string {
    const vehcicles: Risk[] = _get(kydPolicy, 'policyRisks', []);
    let state = '';
    if (vehcicles.length) {
      vehcicles.forEach(vehicle => {
        state = state || _get(vehicle, 'garageLocationAddress.state', '');
      });
    }
    return state;
  }

  private buildAddress(address: any, state: string) {
    return {
      addressLine1: address.line1 || '',
      addressLine2: address.line2 || '',
      city: address.city || '',
      state: state,
      zipCode: address.zip5 || ''
    };
  }

  private buildOperatorsList(policy: EnrollKydPolicyData) {
    const operatorsList: Array<KydSmartphoneEnrollmentOperator> = [];

    policy.policyRoles.forEach(role => {
      const operator: KydSmartphoneEnrollmentOperator = {} as KydSmartphoneEnrollmentOperator;
      // If operatorId doesn't exist, use customerId as key
      const key = role.operatorId ? role.operatorId : role.customerId;
      operator.contactDetail = {} as KydSmartphoneEnrollmentContactDetail;
      operator.customerIdentifier = policy.operatorData[key].customerId;
      // If operator status is not set to "Enrolled", do not add phone number to object. Phone number is required
      // to enroll an operator in KYD.
      if (policy.enrollAll || policy.operatorData[key].status === 'Enrolled') {
        operator.contactDetail.phoneNumber = policy.operatorData[key].phoneNumber;
      }
      operatorsList.push(operator);
    });
    return operatorsList;
  }
}
